<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>VN L2/L3コネクト一覧</span>
        <manual-button path="/nwco-vnl2l3list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search
              :search="vnL2L3Connect.search"
              v-model="vnL2L3Connect.filter"
            />
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn v-permission.config color="primary" @click="addVNL2L3Connect"
              >追加</v-btn
            >
            <v-btn :href="downloadUrl" download color="primary"
              >一覧ダウンロード</v-btn
            >
          </div>
        </v-row>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          primary-key="wnumber"
          :fields="vnL2L3Connect.fields"
          :items="vnL2L3Connect.items"
          :hover="true"
          v-model:table-sort-by="vnL2L3Connect.sortBy"
          :sort-compare="customSortEnumber"
          :sort-compare-key="['enumber']"
          @click:row="showVNL2L3ConnectRef"
          :filter="vnL2L3Connect.filter"
          :filter-option="vnL2L3Connect.filter_option"
          sticky-header="50vh"
          :td-class="cellClass"
        >
          <template #cell(description)="{ item }">
            {{ $filter.emptyTo(item.description, "-") }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import VNL2L3ConnectReference from "@/modals/vnL2L3Connect/VNL2L3ConnectReference.vue";
import VNL2L3ConnectEdit from "@/modals/vnL2L3Connect/VNL2L3ConnectEdit.vue";
import type { VNL2L3Connect } from "@/apis/VNL2L3ConnectApi";
import type { SortCompareFunc } from "@/services/SortService";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "VNL2L3ConnectList",
  data() {
    return {
      isLoaded: false,
      vnl2l3ConnectKeyId: "",
      vnL2L3Connect: {
        sortBy: { key: "enumber", order: "desc" } as SortItem,
        fields: [
          {
            key: "enumber",
            title: "回線番号",
            sortable: true,
            width: "200px",
          },
          {
            key: "wnumber",
            title: "VNコネクト番号",
            sortable: true,
            width: "200px",
          },
          {
            key: "vnConnectName",
            title: "VNコネクト名",
            sortable: true,
            width: "250px",
          },
          {
            key: "l2VN",
            title: "L2VN",
            sortable: true,
            width: "300px",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (_v: string, _k: string, item: VNL2L3Connect) => {
              if (item.l2VnVlanIdType === "SPECIFIED") {
                return `${item.l2VnName}:${item.l2VnCode}(${item.l2VnVlanId})`;
              } else {
                return `${item.l2VnName}:${item.l2VnCode}(${this.$filter.enumTo(item.l2VnVlanIdType, "vlanIdType")})`;
              }
            },
          },
          {
            key: "vlan",
            title: "L2/L3コネクト VLAN",
            sortable: true,
            width: "170px",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (_v: string, _k: string, item: VNL2L3Connect) => {
              if (item.vnl2l3VlanIdType === "SPECIFIED") {
                return `${item.vnl2l3VnVlanId}`;
              } else return `Untag`;
            },
          },
          {
            key: "l3VN",
            title: "L3VPN/VN",
            sortable: true,
            width: "200px",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (_v: string, _k: string, item: VNL2L3Connect) => {
              if (item.l3VpnVnCode.charAt(0) === "B") {
                return `${item.l3VpnVnCode}`;
              } else {
                return `${item.l3VpnVnName}:${item.l3VpnVnCode}`;
              }
            },
          },
          {
            key: "routingType",
            title: "ルーティング種別",
            sortable: true,
            width: "200px",
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (_v: string) => {
              return this.$filter.enumTo(_v, "routingType");
            },
          },
          {
            key: "description",
            title: "備考",
            width: "300px",
          },
        ] as AppTableHeader<VNL2L3Connect>[],
        items: [] as VNL2L3Connect[],
        search: {
          enumber: { type: "text", label: "回線番号" },
          wnumber: { type: "text", label: "VNコネクト番号" },
          vnConnectName: { type: "text", label: "VNコネクト名" },
          l2VN: { type: "text", label: "L2VN" },
          vlan: { type: "text", label: "L2/L3コネクト VLAN" },
          l3VN: { type: "text", label: "L3VPN/VN" },
          routingType: {
            type: "select",
            label: "ルーティング種別",
            items: [
              { value: "BGP4", text: "BGP4" },
              { value: "STATIC", text: "Static" },
              { value: "OSPF", text: "OSPF" },
            ],
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          enumber: "like",
          wnumber: "like",
          vnConnectName: "like",
          l2VN: "like",
          vlan: "like",
          l3VN: "like",
          routingType: "equal",
        } as FilterOption,
      },
      downloadUrl: this.$api.vNL2L3Connect.getVNL2L3connectCsvUrl(),
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  computed: {
    customSortEnumber(): SortCompareFunc<VNL2L3Connect> {
      return this.$service.tableSortCompare(
        this.vnL2L3Connect.sortBy,
        "enumber",
        ["wnumber", "desc"]
      );
    },
  },
  methods: {
    /** 一覧を取得 */
    async load() {
      const data = await this.$api.vNL2L3Connect.getVNL2L3Connects();
      this.vnl2l3ConnectKeyId = data.vnl2l3ConnectKeyId;
      this.vnL2L3Connect.items = data.vnl2l3ConnectList;
    },

    /** 追加 */
    async addVNL2L3Connect() {
      await this.$modal.show(VNL2L3ConnectEdit, {
        vnl2l3ConnectKeyId: this.vnl2l3ConnectKeyId,
      });
      await this.load();
    },

    /** 詳細 */
    async showVNL2L3ConnectRef(item: VNL2L3Connect) {
      await this.$modal.show(VNL2L3ConnectReference, {
        wnumber: item.wnumber,
      });
      await this.load();
    },

    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<VNL2L3Connect>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
